/**
 * AdminVideoEditor — lightweight non-destructive timeline editor for the
 * `video_projects` collection. Hands off heavy export work to the existing
 * /api/video/projects/{id}/output FFmpeg pipeline (already in the backend).
 *
 * Route: /admin/video-editor  (admin auth).
 *
 * MVP scope
 * ---------
 *   • List existing video_projects (status / format).
 *   • Create new project (title, source URL, social format preset).
 *   • Open editor → timeline panel of clip segments [{start, end, label}],
 *     reorder / add / delete / edit each. Persists to `timeline.clips`.
 *   • Quick status transition buttons (draft → editing → review → approved).
 *
 * Heavy lifting (FFmpeg.wasm preview / final export) is intentionally NOT
 * embedded — the existing backend `/output` endpoint takes the timeline
 * and produces the rendered MP4 on the server. This page is the planning
 * & approval surface.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Film, Plus, Save, Trash2, ChevronUp, ChevronDown, Scissors, BellRing, PlayCircle, Download,
} from 'lucide-react';
import VideoReviewDialog from '@/components/broadcast/VideoReviewDialog';
import { getToken } from '@/lib/authToken';
import { MediaUpload } from '@/components/MediaUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const STATUS_COLORS = {
  draft:     'bg-slate-500/20 text-slate-300',
  editing:   'bg-indigo-500/20 text-indigo-300',
  review:    'bg-amber-500/20 text-amber-300',
  approved:  'bg-emerald-500/20 text-emerald-300',
  exported:  'bg-emerald-500/20 text-emerald-300',
  published: 'bg-fuchsia-500/20 text-fuchsia-300',
  archived:  'bg-slate-700/40 text-slate-500',
};

// ============== Editor panel ==============
const TimelineEditor = ({ project, onClose }) => {
  const qc = useQueryClient();
  const [clips, setClips] = useState(() => project?.timeline?.clips || []);
  const [notes, setNotes] = useState(project?.notes || '');

  useEffect(() => {
    setClips(project?.timeline?.clips || []);
    setNotes(project?.notes || '');
  }, [project]);

  const addClip = () => {
    const last = clips[clips.length - 1];
    setClips([...clips, {
      start: last ? Number(last.end) : 0,
      end: last ? Number(last.end) + 10 : 10,
      label: `Clip ${clips.length + 1}`,
    }]);
  };

  const updateClip = (idx, field, value) => {
    setClips(clips.map((c, i) => i === idx
      ? { ...c, [field]: field === 'label' ? value : Number(value) }
      : c));
  };

  const removeClip = (idx) => setClips(clips.filter((_, i) => i !== idx));

  const move = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= clips.length) return;
    const copy = [...clips];
    [copy[idx], copy[ni]] = [copy[ni], copy[idx]];
    setClips(copy);
  };

  const totalDur = clips.reduce((s, c) => s + Math.max(0, Number(c.end) - Number(c.start)), 0);

  const save = useMutation({
    mutationFn: async () => axios.put(
      `${API}/api/video/projects/${project.id}`,
      { timeline: { clips }, notes }, { headers: authH() }
    ),
    onSuccess: () => {
      toast.success('Timeline saved');
      qc.invalidateQueries({ queryKey: ['video-projects'] });
      qc.invalidateQueries({ queryKey: ['video-project', project.id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Save failed'),
  });

  const setStatus = useMutation({
    mutationFn: async (status) => axios.post(
      `${API}/api/video/projects/${project.id}/status`,
      { status }, { headers: authH() }
    ),
    onSuccess: (_, status) => {
      toast.success(`Status → ${status}`);
      qc.invalidateQueries({ queryKey: ['video-projects'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Status change failed'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Film className="h-5 w-5 text-indigo-400" /> {project.title}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {project.social_format || 'no preset'} ·
            <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${STATUS_COLORS[project.status] || ''}`}>
              {project.status}
            </span>
            {project.source_url && (
              <span className="ml-2 text-slate-500 truncate inline-block max-w-[300px] align-middle">
                source: {project.source_url}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Timeline visualisation */}
        {clips.length > 0 && (
          <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-2 mb-2">
            <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Timeline · {totalDur.toFixed(1)}s</div>
            <div className="flex h-7 rounded-md overflow-hidden bg-slate-900 ring-1 ring-slate-700">
              {clips.map((c, i) => {
                const dur = Math.max(0, Number(c.end) - Number(c.start));
                const pct = totalDur > 0 ? (dur / totalDur) * 100 : 0;
                return (
                  <div key={i} title={c.label} style={{ width: `${pct}%` }}
                       className="border-r border-slate-950/60 bg-gradient-to-r from-indigo-500/50 to-fuchsia-500/40 flex items-center justify-center text-[10px] text-white/90 truncate px-1">
                    {c.label}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Clips list */}
        <div className="space-y-2 max-h-[280px] overflow-y-auto" data-testid="video-clips-list">
          {clips.map((c, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-slate-800/30 rounded-md p-2">
              <Input value={c.label} onChange={e => updateClip(idx, 'label', e.target.value)}
                     className="col-span-5 bg-slate-900 border-slate-700 text-sm"
                     data-testid={`clip-label-${idx}`} placeholder="Label" />
              <Input type="number" step="0.1" value={c.start} onChange={e => updateClip(idx, 'start', e.target.value)}
                     className="col-span-2 bg-slate-900 border-slate-700 text-sm"
                     data-testid={`clip-start-${idx}`} placeholder="Start (s)" />
              <Input type="number" step="0.1" value={c.end} onChange={e => updateClip(idx, 'end', e.target.value)}
                     className="col-span-2 bg-slate-900 border-slate-700 text-sm"
                     data-testid={`clip-end-${idx}`} placeholder="End (s)" />
              <div className="col-span-3 flex justify-end gap-1">
                <Button size="icon" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === clips.length - 1}>
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => removeClip(idx)}
                         className="text-rose-400 hover:text-rose-300"
                         data-testid={`clip-remove-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {clips.length === 0 && (
            <div className="text-center text-slate-500 text-sm py-6">
              No clips yet — click <strong>Add clip</strong> below.
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={addClip} data-testid="video-add-clip">
            <Scissors className="h-4 w-4 mr-1.5" /> Add clip
          </Button>
        </div>

        <div>
          <Label className="text-xs text-slate-500">Notes</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                     className="bg-slate-800/40 border-slate-700 text-sm"
                     placeholder="Director notes, captions intent…" rows={2}
                     data-testid="video-notes" />
        </div>

        <DialogFooter className="flex flex-wrap gap-2 justify-between">
          <div className="flex gap-1.5">
            {['editing', 'review', 'approved'].map(s => (
              <Button key={s} variant="outline" size="sm" disabled={project.status === s}
                       onClick={() => setStatus.mutate(s)}
                       className="text-xs"
                       data-testid={`video-status-${s}`}>
                → {s}
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}
                     data-testid="video-save-timeline">
              <Save className="h-4 w-4 mr-1.5" />
              {save.isPending ? 'Saving…' : 'Save timeline'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== New project dialog ==============
const NewProjectDialog = ({ open, onOpenChange, presets }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: '', tenant_id: '', social_format: '', source_url: '',
  });
  const create = useMutation({
    mutationFn: async () => (await axios.post(
      `${API}/api/video/projects`, form, { headers: authH() }
    )).data,
    onSuccess: () => {
      toast.success('Project created');
      onOpenChange(false);
      setForm({ title: '', tenant_id: '', social_format: '', source_url: '' });
      qc.invalidateQueries({ queryKey: ['video-projects'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Create failed'),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
        <DialogHeader>
          <DialogTitle>New video project</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label className="text-xs text-slate-500">Title</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                   className="bg-slate-800/60 border-slate-700" data-testid="video-new-title" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Tenant ID</Label>
            <Input value={form.tenant_id} onChange={e => setForm({ ...form, tenant_id: e.target.value })}
                   className="bg-slate-800/60 border-slate-700" data-testid="video-new-tenant" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Social format</Label>
            <Select value={form.social_format} onValueChange={(v) => setForm({ ...form, social_format: v })}>
              <SelectTrigger className="bg-slate-800/60 border-slate-700"
                              data-testid="video-new-format"><SelectValue placeholder="Pick a preset…" /></SelectTrigger>
              <SelectContent>
                {(presets || []).map(p => (
                  <SelectItem key={p.key} value={p.key}>{p.label} · {p.aspect}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <MediaUpload value={form.source_url} onChange={(url) => setForm({ ...form, source_url: url })} purpose="video" label="Source recording (optional)" testId="video-new-source" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => create.mutate()}
                   disabled={create.isPending || !form.title || !form.tenant_id}
                   data-testid="video-new-save">
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== Main page ==============
const AdminVideoEditor = () => {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [reviewId, setReviewId] = useState(null);

  const { data: projData } = useQuery({
    queryKey: ['video-projects'],
    queryFn: async () => (await axios.get(
      `${API}/api/video/projects?limit=50`, { headers: authH() }
    )).data,
  });
  const { data: presetData } = useQuery({
    queryKey: ['video-presets'],
    queryFn: async () => (await axios.get(`${API}/api/video/presets`)).data,
  });

  const projects = projData?.projects || [];
  const presets = presetData?.presets || [];
  const current = projects.find(p => p.id === editingId);
  const waiting = projects.filter(p => p.status === 'review');

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
              <Film className="h-6 w-6 text-indigo-400" /> Video Editor
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Plan timelines, manage approvals, hand off final renders to the FFmpeg pipeline.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} data-testid="video-new-project">
            <Plus className="h-4 w-4 mr-1.5" /> New project
          </Button>
        </header>

        {waiting.length > 0 && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex flex-wrap items-center gap-3"
               data-testid="video-review-banner">
            <BellRing className="h-4 w-4 text-amber-300 shrink-0" />
            <p className="text-sm text-amber-100 min-w-0 flex-1">
              {waiting.length === 1
                ? <><strong>{waiting[0].title}</strong> is finished and waiting for your review.</>
                : <><strong>{waiting.length} videos</strong> are finished and waiting for your review.</>}
              <span className="block text-xs text-amber-200/70">
                Watch it, approve it or ask for changes — and download it ready to post.
              </span>
            </p>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => setReviewId(waiting[0].id)}
                    data-testid="video-review-banner-btn">
              <PlayCircle className="h-4 w-4 mr-1.5" /> Review {waiting.length > 1 ? 'the first' : 'now'}
            </Button>
          </div>
        )}

        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-200">Projects ({projects.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left px-4 py-2">Title</th>
                  <th className="text-left px-4 py-2">Format</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Updated</th>
                  <th className="text-right px-4 py-2">Clips</th>
                  <th className="text-right px-4 py-2">Review</th>
                </tr>
              </thead>
              <tbody data-testid="video-projects-table">
                {projects.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                    No projects yet — click <strong>New project</strong>.
                  </td></tr>
                )}
                {projects.map(p => (
                  <tr key={p.id} onClick={() => setEditingId(p.id)}
                       className="border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer transition"
                       data-testid={`video-row-${p.id}`}>
                    <td className="px-4 py-2 text-slate-100">{p.title}</td>
                    <td className="px-4 py-2 text-slate-400 text-xs">{p.social_format || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${STATUS_COLORS[p.status] || ''}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 hidden md:table-cell">
                      {p.updated_at ? new Date(p.updated_at).toLocaleString() : ''}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-400">
                      {(p.timeline?.clips || []).length}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {((p.output_files?.length || 0) > 0 || p.status === 'review') && (
                        <Button size="sm"
                                variant={p.status === 'review' ? 'default' : 'outline'}
                                className={p.status === 'review'
                                  ? 'bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs'
                                  : 'h-7 text-xs border-slate-700 text-slate-300'}
                                onClick={(e) => { e.stopPropagation(); setReviewId(p.id); }}
                                data-testid={`video-review-${p.id}`}>
                          {p.status === 'review'
                            ? <><PlayCircle className="h-3.5 w-3.5 mr-1" /> Review &amp; download</>
                            : <><Download className="h-3.5 w-3.5 mr-1" /> Watch &amp; download</>}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <NewProjectDialog open={creating} onOpenChange={setCreating} presets={presets} />
        {current && <TimelineEditor project={current} onClose={() => setEditingId(null)} />}
        {reviewId && (
          <VideoReviewDialog projectId={reviewId} presets={presets}
                             onClose={() => setReviewId(null)} />
        )}
      </div>
    </div>
  );
};

export default AdminVideoEditor;
